import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  useNavigate,
} from 'react-router-dom';

import { BatchJobList } from '../components/batch-job-list/batch-job-list';
import { PageContainer } from '../components/page-container';
import { RedirectFullPageBox } from '../components/redirect/redirect-full-page-box';
import { SignInNavigateDestination } from '../components/sign-in-form/sign-in-form';
import { AppLayout } from '../layout/app-layout';
import { PasswordReset } from '../pages/password-reset/password-reset';
import { RequestPasswordResetLink } from '../pages/request-password-reset-link/request-password-reset-link';
import { SignIn } from '../pages/sign-in/sign-in';
import {
  AuthCallbackErrorRoute,
  authCallbackLoader,
  AuthCallbackRoute,
} from '../routing/auth-callback-route';
import { ProtectedRoute } from '../routing/protected-route';
import { RedirectRoute } from '../routing/redirect-route';
import { RouteErrorBoundary } from '../routing/route-error-boundary';
import { UnsavedChangesBlocker } from '../routing/unsaved-changes-blocker';
import { AppState } from '../state/app-state';
import { useOptionalAuthState } from '../state/auth-state';
import { UserCompaniesStateProvider } from '../state/user-companies-state';
import { WebsocketStateProvider } from '../state/websocket-state';
import { AccessTokenRedirectRoute } from './access-token-redirect-route';
import { AccessTokensRoute } from './access-tokens-route';
import { AccountsPage } from './accounts';
import { AdminUsersRoute } from './admin-users-route';
import {
  CompaniesLayout,
  CompaniesListRoute,
  CompanyAdminsRoute,
  CompanyDetailLayout,
  CompanyLicensesRoute,
  CompanyOverviewRoute,
  PendingProjectTransfersRoute,
} from './companies';
import { DevToolsClientVersionRoute } from './dev-tools-client-version-route';
import { NewCompanyAdminRoute } from './new-company-admin';
import { PlaceholderPage } from './placeholder';
import { ProfileRoute } from './profile';
import {
  ProjectEmailRoute,
  ProjectFileManagerRoute,
  ProjectGeodataGcpRoute,
  ProjectGeodataRoute,
  ProjectGeodataUploadRoute,
  ProjectIntegrationsRoute,
  ProjectLayout,
  ProjectOverviewRoute,
  ProjectSettingsRoute,
  ProjectTimelineRoute,
  ProjectTransferRequestRoute,
  ProjectUsersRoute,
} from './project';
import { ProjectsLayout, ProjectsListRoute } from './projects';
import { RegisterCompanyRoute, RegisterDesignCompanyRoute } from './register-company';
import { SharedFolderRoute } from './shared-folder';
import { SharedViewRoute } from './shared-view';
import { SkyviewRoute } from './skyview';
import { VerifyRoute } from './verify';

const SignInRoute = () => {
  const navigate = useNavigate();
  const authState = useOptionalAuthState();

  React.useEffect(() => {
    if (authState?.status === 'authenticated') {
      void navigate('/redirect', { replace: true });
    }
  }, [authState?.status, navigate]);

  const handleNavigate = React.useCallback(
    (destination: SignInNavigateDestination) => {
      switch (destination) {
        case 'redirect':
          void navigate('/redirect', { replace: true });
          break;
        case 'requestpasswordchange':
          void navigate('/requestpasswordchange');
          break;
        default:
          break;
      }
    },
    [navigate],
  );

  return <SignIn onNavigate={handleNavigate} />;
};

const RequestPasswordResetLinkRoute = () => {
  const navigate = useNavigate();
  return <RequestPasswordResetLink onGoToLoginPage={() => void navigate('/signin')} />;
};

const UnexpectedErrorPage = () => {
  const { t } = useTranslation();
  return <PlaceholderPage description={t('unexpectedError', { ns: 'common' })} />;
};

const CompaniesPlaceholderPage = () => {
  const { t } = useTranslation();
  return <PlaceholderPage description={t('menu.companies.description', { ns: 'navigation' })} />;
};

const AccountsRoute = () => {
  const { t } = useTranslation();
  return (
    <PageContainer title={t('menu.accounts.title', { ns: 'navigation' })}>
      <AccountsPage />
    </PageContainer>
  );
};

const BatchJobsRoute = () => {
  const { t } = useTranslation();
  return (
    <PageContainer title={t('menu.jobs.title', { ns: 'navigation' })}>
      <BatchJobList />
    </PageContainer>
  );
};

const ProfilePageRoute = () => {
  const { t } = useTranslation();
  return (
    <PageContainer title={t('profile', { ns: 'common' })}>
      <ProfileRoute />
    </PageContainer>
  );
};

const RootLayout = () => (
  <WebsocketStateProvider>
    <UserCompaniesStateProvider>
      <UnsavedChangesBlocker />
      <Outlet />
    </UserCompaniesStateProvider>
  </WebsocketStateProvider>
);

const createRootRoutes = () =>
  createRoutesFromElements(
    <Route element={<RootLayout />} errorElement={<RouteErrorBoundary />} path="/">
      <Route element={<SignInRoute />} path="signin" />
      <Route element={<RequestPasswordResetLinkRoute />} path="requestpasswordchange" />
      <Route element={<PasswordReset />} path="passwordreset" />
      <Route element={<RegisterCompanyRoute />} path="registercompany" />
      <Route element={<RegisterDesignCompanyRoute />} path="register-design" />
      <Route element={<VerifyRoute />} path="verify" />
      <Route element={<NewCompanyAdminRoute />} path="newcompanyadmin" />
      <Route element={<SharedFolderRoute />} path="shared-folder/:id" />
      <Route element={<SharedViewRoute />} path="viewer/shared/:sharedViewId" />
      <Route
        element={
          <ProtectedRoute redirectTo="/signin">
            <AppLayout />
          </ProtectedRoute>
        }
        path="*"
      >
        <Route element={<Navigate replace={true} to="projects" />} index={true} />
        <Route element={<ProjectsLayout />} path="projects">
          <Route element={<ProjectsListRoute />} index={true} />
          <Route element={<ProjectLayout />} path=":projectId">
            <Route element={<ProjectOverviewRoute />} index={true} />
            <Route element={<SkyviewRoute />} path="viewer" />
            <Route element={<ProjectIntegrationsRoute />} path="integrations" />
            <Route element={<ProjectEmailRoute />} path="email" />
            <Route element={<ProjectGeodataRoute />} path="geodata" />
            <Route element={<ProjectGeodataUploadRoute />} path="geodata/upload" />
            <Route element={<ProjectGeodataGcpRoute />} path="geodata/:geodataId/viewer" />
            <Route element={<ProjectUsersRoute />} path="users" />
            <Route element={<ProjectTimelineRoute />} path="timeline" />
            <Route element={<ProjectFileManagerRoute />} path="filemanager" />
            <Route element={<ProjectSettingsRoute />} path="settings" />
            <Route element={<ProjectTransferRequestRoute />} path="transfer" />
            <Route element={<UnexpectedErrorPage />} path="*" />
          </Route>
          <Route element={<UnexpectedErrorPage />} path="*" />
        </Route>
        <Route element={<AdminUsersRoute />} path="users" />
        <Route element={<CompaniesLayout />} path="companies">
          <Route element={<CompaniesListRoute />} index={true} />
          <Route element={<CompanyDetailLayout />} path=":companyId">
            <Route element={<CompanyOverviewRoute />} index={true} />
            <Route element={<CompanyLicensesRoute />} path="licenses" />
            <Route element={<CompanyAdminsRoute />} path="admins" />
            <Route element={<PendingProjectTransfersRoute />} path="pending-transfers" />
            <Route element={<CompaniesPlaceholderPage />} path="*" />
          </Route>
          <Route element={<CompaniesPlaceholderPage />} path="*" />
        </Route>
        <Route element={<AccountsRoute />} path="accounts" />
        <Route element={<BatchJobsRoute />} path="jobs" />
        <Route element={<AccessTokensRoute />} path="access-tokens" />
        <Route element={<DevToolsClientVersionRoute />} path="dev-tools/client-version" />
        <Route element={<ProfilePageRoute />} path="profile" />
      </Route>
      <Route element={<RedirectRoute />} path="redirect" />
      <Route
        element={<AuthCallbackRoute />}
        errorElement={<AuthCallbackErrorRoute />}
        // The browser arrives here from Zitadel as a full page load, so the router hydrates on this
        // route with the exchange still in flight. Without a fallback the user sees a blank page.
        hydrateFallbackElement={<RedirectFullPageBox />}
        loader={authCallbackLoader}
        path="auth/callback"
      />
      <Route element={<AccessTokenRedirectRoute />} path="redirectaccess/:token" />
      <Route element={<Navigate replace={true} to="/projects" />} path="*" />
    </Route>,
  );

export const Root = () => {
  /**
   *  Built exactly once, and deliberately during render rather than at module scope.
   *
   *  Once, because rebuilding a router re-runs the matched route's loader, remounts the whole tree
   *  and discards navigation state. It used to be memoised on `t`, which is not referentially
   *  stable — i18next replaces it when a namespace finishes loading or the language changes — so
   *  the router was rebuilt mid-session. On `/auth/callback` that redeemed the single-use
   *  authorization code a second time and failed the sign-in. Nothing reactive is captured now:
   *  routes that need translations call `useTranslation` themselves, when they render.
   *
   *  During render, because `createBrowserRouter` starts the initial navigation immediately, and a
   *  loader that runs at module-evaluation time is too early: `main.tsx` publishes the Vite
   *  environment to `window.__SV_ENV` in its body, and ES imports are evaluated before it. At
   *  module scope `authCallbackLoader` therefore read an unset `ZITADEL_ISSUER` and threw.
   *
   *  `useState` rather than `useMemo`: React may discard a memo, and losing the router mid-session
   *  would resurrect exactly the bug this avoids.
   */
  const [router] = React.useState(() => createBrowserRouter(createRootRoutes()));

  return (
    <AppState>
      <RouterProvider router={router} />
    </AppState>
  );
};
